import { useEffect, useState } from 'react';
import { ThemeToggle } from '../components/ThemeToggle';
import { hideBanner, showLobbyBanner } from '../native/ads';
import { isNative } from '../native/platform';
import { Link, useNavigate } from 'react-router';
import type { DailyRewardStatusDto, GameSummaryDto, MissionDto } from '@cardhub/protocol';
import { api } from '../api/client';
import { GameArt } from '../cards/PlayingCard';
import { InstallApp } from '../components/InstallApp';
import { RewardedAdPlayer, useRewardedAd } from '../components/RewardedAd';
import { Avatar, Coins, ErrorState, Icon, Modal, Progress, Skeleton } from '../components/ui';
import { useFormat, useT } from '../i18n';
import { useAsync } from '../lib/hooks';
import { CARD_BACKS, TABLE_FELTS, type CosmeticKind } from '@cardhub/protocol';
import { useGame } from '../store/game';
import { ShopItem, useCosmetics } from './Shop';
import { saveSettings, useSession } from '../store/session';
import { useSettings } from '../store/settings';
import { mergeRecent, useRecentGames } from '../store/recentGames';
import { filterGames } from '../lib/gameSearch';
import { loadGames } from '../offline/catalog';
import { pendingClaimCount } from '../offline/claims';

export const GAME_ART: Record<string, string[]> = {
  CALL_BREAK: ['AS', 'KS', 'QS'],
  SPADES: ['AS', 'AH', 'KS'],
  TARNEEB: ['AH', 'KH', 'QH'],
  HEARTS: ['QS', 'AH', 'KH'],
  FIVE_HUNDRED: ['JK:0', 'JS', 'AS'],
  JUDGEMENT: ['AH', 'KH', '5C'],
  RUMMY: ['7H', '8H', 'JK:0'],
  EUCHRE: ['JS', 'JC', 'AS'],
  GIN_RUMMY: ['7S', '8S', '9S'],
  TWENTY_NINE: ['JH', '9H', 'AH'],
  CRAZY_EIGHTS: ['8S', '8H', '8D'],
  CRIBBAGE: ['5H', '5D', '5S'],
  BLACKJACK: ['AS', 'KH'],
  BHABHI: ['AS', 'KS', 'QS'],
  PINOCHLE: ['QS', 'JD', 'AH'],
  ANDAR_BAHAR: ['7H', '7S', '7D'],
  BRIDGE: ['AS', 'KS', 'QS'],
  SEVENS: ['6H', '7H', '8H'],
  SCOPA: ['7D', 'KD', 'AD'],
  BACCARAT: ['9S', 'KH'],
  BLUFF: ['AS', 'AH', 'AD'],
  DRAGON_TIGER: ['KS', 'QH'],
  FLUSH: ['AH', 'KH', 'QH'],
  COURT_PIECE: ['AS', 'KS', 'JS'],
  DEHLA_PAKAD: ['TS', 'TH', 'TD'],
  WHIST: ['AS', 'QS', 'JS'],
  SEEP: ['9C', 'TD', 'KH'],
  BELOTE: ['JS', '9S', 'AS'],
  DURAK: ['6S', '9H', 'AD'],
  PRESIDENT: ['2S', 'AH', 'KD'],
  BRISCOLA: ['AS', '3H', 'KD'],
  CASINO_WAR: ['KS', 'KH'],
  THREE_CARD_POKER: ['AS', 'KH', 'QD'],
  GO_FISH: ['7S', '7H', '7D'],
  TRESSETTE: ['3S', '2H', 'AD'],
  RED_DOG: ['2S', 'KH'],
  CANASTA: ['2S', 'JK:0', 'AH'],
  SKAT: ['JC', 'JS', 'AD'],
  PAI_GOW: ['AS', 'KH', 'QD'],
  BIG_TWO: ['2S', '2H', 'AD'],
  WAR: ['KS', 'QH'],
  RUMMY_500: ['7S', '8S', '9S'],
  BRAG: ['AH', 'AD', 'AC'],
  PITCH: ['AS', 'JS', 'TS'],
  HAZARI: ['AS', 'KS', 'QS'],
  TEEN_PATTI: ['AH', 'AD', 'AC'],
  POKER: ['AS', 'KS', 'QS', 'JS', 'TS'],
  MARRIAGE: ['7H', '8H', '9H'],
};

function FavButton({ type, fav, onToggle }: { type: string; fav: boolean; onToggle: () => void }) {
  const t = useT();
  const game = t(`game.${type}` as 'game.CALL_BREAK');
  return (
    <button className="fav-btn" aria-pressed={fav} aria-label={fav ? t('games.favRemove', { game }) : t('games.favAdd', { game })} onClick={onToggle}>
      <svg width="20" height="20" viewBox="0 0 24 24" fill={fav ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={2} strokeLinejoin="round" aria-hidden>
        <path d="M12 2l3.1 6.3 6.9 1-5 4.9 1.2 6.8L12 17.8 5.8 21l1.2-6.8-5-4.9 6.9-1z" />
      </svg>
    </button>
  );
}

function GameCard({ g, fav, onFav, compact = false }: { g: GameSummaryDto; fav: boolean; onFav: () => void; compact?: boolean }) {
  const t = useT();
  const f = useFormat();
  const quickPlay = useGame((s) => s.quickPlayGame);
  const nav = useNavigate();
  return (
    <article className={`game-card ${compact ? 'compact' : ''} ${g.enabled ? '' : 'disabled'}`} data-game={g.type} aria-label={t(`game.${g.type}`)}>
      <FavButton type={g.type} fav={fav} onToggle={onFav} />
      {!compact && <GameArt cards={GAME_ART[g.type]!.slice(0, 3)} />}
      <div>
        <h3>{t(`game.${g.type}`)}</h3>
        {!compact && (
          <div className="meta">
            <span>{t('common.online', { n: f.compact(g.onlinePlayers) })}</span>
            <span>{t('lobby.tables', { n: g.tables.length })}</span>
            <span>{t('tables.players', { min: g.minPlayers, max: g.maxPlayers })}</span>
            <span>
              {t('lobby.entry', { min: f.compact(g.minEntry), max: f.compact(g.maxEntry) })}
            </span>
          </div>
        )}
      </div>
      <div className="actions">
        <button className="btn play" disabled={!g.enabled} onClick={() => void quickPlay(g.type)}>
          <Icon name="play" size={18} /> {t('lobby.quickPlay')}
        </button>
        <button className="btn more" onClick={() => nav(`/games/${g.type}`)} aria-label={`${t('tables.title')} — ${t(`game.${g.type}`)}`}>
          <Icon name="settings" size={18} />
        </button>
      </div>
    </article>
  );
}

export function Home() {
  const t = useT();
  const f = useFormat();
  const nav = useNavigate();
  const user = useSession((s) => s.user)!;
  const balance = useSession((s) => s.balance);
  const unread = useSession((s) => s.unread);
  const ws = useSession((s) => s.ws);
  const pending = pendingClaimCount();
  const cosmetics = useCosmetics();
  const shopPicks: { kind: CosmeticKind; id: string; price: number; owned: boolean }[] = [
    ...CARD_BACKS.map((c) => ({ kind: 'CARD_BACK' as const, id: c.id, price: c.price, owned: cosmetics.owned?.cardBacks.includes(c.id) ?? c.price === 0 })),
    ...TABLE_FELTS.map((c) => ({ kind: 'TABLE_FELT' as const, id: c.id, price: c.price, owned: cosmetics.owned?.tableFelts.includes(c.id) ?? c.price === 0 })),
  ]
    .filter((i) => !i.owned)
    .slice(0, 6);
  const boot = useSession((s) => s.boot);
  const games = useAsync(() => loadGames(), []);
  const daily = useAsync(() => api.get<DailyRewardStatusDto>('/api/v1/rewards/daily'), []);
  const missions = useAsync(() => api.get<MissionDto[]>('/api/v1/missions'), []);
  const current = useAsync(() => api.get<{ tableId: string; gameType: string } | null>('/api/v1/tables/current'), []);
  const recent = useAsync(() => api.get<{ matchId: string; gameType: string; place: number | null; coinsDelta: number; startedAt: string }[]>('/api/v1/me/games'), []);
  const ad = useRewardedAd('lobby_free_coins');
  const [joinOpen, setJoinOpen] = useState(false);
  const [code, setCode] = useState('');
  const [query, setQuery] = useState('');
  const favorites = useSettings((s) => s.settings.favoriteGames);
  const localRecent = useRecentGames((s) => s.byUser[user.id]);
  const toggleFavorite = (type: string) =>
    void saveSettings({ favoriteGames: favorites.includes(type as GameSummaryDto['type']) ? favorites.filter((x) => x !== type) : [...favorites, type as GameSummaryDto['type']] });
  const nameOf = (type: string) => t(`game.${type}` as 'game.CALL_BREAK');
  const byType = new Map((games.data ?? []).map((g) => [g.type, g]));
  const pick = (types: readonly string[]) => types.map((type) => byType.get(type as GameSummaryDto['type'])).filter((g): g is GameSummaryDto => !!g);
  const favoriteGames = pick(favorites);
  const recentGames = pick(mergeRecent(localRecent ?? [], (recent.data ?? []).map((r) => r.gameType)));
  const searching = query.trim().length > 0;
  const shown = games.data ? (searching ? filterGames(games.data, query, nameOf) : games.data) : [];
  useEffect(() => {
    if (!isNative) return;
    let on = true;
    void api.get<{ placements: { ad_type: string; placement: string; enabled: boolean }[] }>('/api/v1/ads/config').then((c) => {
      if (on && c.placements.some((p) => p.ad_type === 'BANNER' && p.placement === 'lobby_bottom' && p.enabled)) void showLobbyBanner(68);
    }).catch(() => undefined);
    return () => {
      on = false;
      void hideBanner();
    };
  }, []);
  const claimable = missions.data?.filter((m) => m.status === 'COMPLETED').length ?? 0;
  const xpPrev = 50 * (user.level - 1) * user.level;

  return (
    <main className="page">
      <header className="hero">
        <Link to="/profile" aria-label={t('nav.profile')}>
          <Avatar avatar={user.avatar} size={52} name={user.username} />
        </Link>
        <div className="col" style={{ gap: 4 }}>
          <span className="name ellipsis">{user.username}</span>
          <div className="row" style={{ gap: 8 }}>
            <span className="pill accent">{t('common.level', { n: user.level })}</span>
            <div className="xp-bar">
              <Progress value={user.xp - xpPrev} max={user.xpForNextLevel - xpPrev} label="XP" />
            </div>
          </div>
        </div>
        <div className="wallet-chip" aria-label={`${balance} ${t('common.coins')}`}>
          <Coins amount={balance} />
          <button className="plus" onClick={() => nav('/store')} aria-label={t('lobby.store')}>
            +
          </button>
        </div>
        <ThemeToggle />
        <Link to="/notifications" className="icon-btn" aria-label={t('notif.title')}>
          <Icon name="bell" />
          {unread > 0 && <span className="badge" style={{ position: 'absolute', top: -4, right: -4 }}>{unread > 9 ? '9+' : unread}</span>}
        </Link>
      </header>

      <InstallApp />

      {ws !== 'open' && (
        <div className="card spread" style={{ marginBottom: 16, borderColor: 'var(--accent)' }} role="status">
          <span>
            <strong>{t('offline.title')}</strong>
            <br />
            <span className="dim" style={{ fontSize: 'var(--fs-sm)' }}>{t('offline.body')}</span>
            {pending > 0 && (
              <>
                <br />
                <span className="dim" style={{ fontSize: 'var(--fs-sm)' }}>{t('offline.pending', { n: pending })}</span>
              </>
            )}
          </span>
        </div>
      )}

      {boot?.events.map((e) => (
        <div key={e.code} className="card glass" style={{ marginBottom: 12, borderColor: e.color ?? undefined }}>
          <strong>🎉 {e.name}</strong>
          <div className="dim" style={{ fontSize: 'var(--fs-sm)' }}>{e.description}</div>
        </div>
      ))}
      {boot?.announcements.map((a) => (
        <div key={a.id} className="card" style={{ marginBottom: 12 }} role="note">
          <strong>{a.title}</strong>
          <div className="dim" style={{ fontSize: 'var(--fs-sm)' }}>{a.body}</div>
        </div>
      ))}

      {current.data && (
        <div className="card spread" style={{ marginBottom: 16, borderColor: 'var(--accent)' }}>
          <span>
            <strong>{t('lobby.continueGame')}</strong>
            <br />
            <span className="dim">{t(`game.${current.data.gameType}` as 'game.CALL_BREAK')}</span>
          </span>
          <button className="btn primary" onClick={() => nav(`/table/${current.data!.tableId}`)}>
            {t('lobby.rejoin')}
          </button>
        </div>
      )}

      <div className="quick-row" role="list">
        <Link to="/rewards" className="quick-tile" role="listitem">
          <span className="ico">🎁</span>
          {t('lobby.daily')}
          {daily.data?.canClaim && <span className="badge">1</span>}
        </Link>
        <Link to="/rewards?tab=missions" className="quick-tile" role="listitem">
          <span className="ico">🎯</span>
          {t('lobby.missions')}
          {claimable > 0 && <span className="badge">{claimable}</span>}
        </Link>
        <button className="quick-tile" role="listitem" onClick={() => void ad.start()} disabled={ad.busy || !boot?.flags.REWARDED_ADS_ENABLED}>
          <span className="ico">📺</span>
          {t('lobby.watchAd')}
        </button>
        <Link to="/shop" className="quick-tile" role="listitem">
          <span className="ico">🛍️</span>
          Shop
        </Link>
        <Link to="/store" className="quick-tile" role="listitem">
          <span className="ico">💰</span>
          {t('lobby.store')}
        </Link>
        <Link to="/rooms/new" className="quick-tile" role="listitem">
          <span className="ico">🔒</span>
          {t('lobby.privateRoom')}
        </Link>
        <button className="quick-tile" role="listitem" onClick={() => setJoinOpen(true)}>
          <span className="ico">🔑</span>
          {t('lobby.joinRoom')}
        </button>
        <Link to="/tournaments" className="quick-tile" role="listitem">
          <span className="ico">🏆</span>
          {t('lobby.tournaments')}
        </Link>
        <Link to="/settings" className="quick-tile" role="listitem">
          <span className="ico">⚙️</span>
          {t('settings.title')}
        </Link>
      </div>

      <h2 className="section-title">{t('lobby.games')}</h2>
      <div className="game-search" role="search">
        <Icon name="search" size={18} />
        <input
          type="search"
          className="input"
          placeholder={t('games.search')}
          aria-label={t('games.search')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoComplete="off"
          enterKeyHint="search"
        />
        {query && (
          <button className="clear icon-btn" onClick={() => setQuery('')} aria-label={t('games.clear')}>
            <Icon name="close" size={16} />
          </button>
        )}
      </div>
      {games.loading && (
        <div className="games-grid">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} h={190} r={26} />
          ))}
        </div>
      )}
      {games.error && <ErrorState message={games.error} onRetry={games.reload} />}
      {games.data && !searching && favoriteGames.length > 0 && (
        <section aria-label={t('games.favourites')}>
          <h3 className="sub-title">★ {t('games.favourites')}</h3>
          <div className="shortcut-row">
            {favoriteGames.map((g) => (
              <GameCard key={g.type} g={g} compact fav onFav={() => toggleFavorite(g.type)} />
            ))}
          </div>
        </section>
      )}
      {games.data && !searching && recentGames.length > 0 && (
        <section aria-label={t('games.recentlyPlayed')}>
          <h3 className="sub-title">{t('games.recentlyPlayed')}</h3>
          <div className="shortcut-row">
            {recentGames.map((g) => (
              <GameCard key={g.type} g={g} compact fav={favorites.includes(g.type)} onFav={() => toggleFavorite(g.type)} />
            ))}
          </div>
        </section>
      )}
      {games.data && (
        <>
          {!searching && (favoriteGames.length > 0 || recentGames.length > 0) && <h3 className="sub-title">{t('games.all')}</h3>}
          {searching && (
            <p className="dim" role="status" style={{ fontSize: 'var(--fs-sm)', margin: '0 0 var(--s-3)' }}>
              {t('games.results', { n: shown.length })}
            </p>
          )}
          {shown.length === 0 ? (
            <div className="card center" role="status" style={{ gap: 8, padding: 24, textAlign: 'center' }}>
              <span>{t('games.noMatch', { q: query.trim() })}</span>
              <button className="btn" onClick={() => setQuery('')}>
                {t('games.clear')}
              </button>
            </div>
          ) : (
            <div className="games-grid">
              {shown.map((g) => (
                <GameCard key={g.type} g={g} fav={favorites.includes(g.type)} onFav={() => toggleFavorite(g.type)} />
              ))}
            </div>
          )}
        </>
      )}

      <h2 className="section-title">
        Shop
        <Link to="/shop" className="dim" style={{ fontSize: 'var(--fs-sm)' }}>
          {t('common.seeAll')}
        </Link>
      </h2>
      <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 8, scrollSnapType: 'x mandatory' }}>
        {shopPicks.map((it) => (
          <div key={`${it.kind}-${it.id}`} style={{ flex: '0 0 230px', scrollSnapAlign: 'start' }}>
            <ShopItem kind={it.kind} id={it.id} cents={it.price} owned={it.owned} product={cosmetics.productFor(it.kind, it.id)} enabled={cosmetics.enabled} onBought={cosmetics.reload} />
          </div>
        ))}
        <Link to="/shop" className="card center" style={{ flex: '0 0 140px', scrollSnapAlign: 'start' }}>
          Browse all →
        </Link>
      </div>

      {recent.data && recent.data.length > 0 && (
        <>
          <h2 className="section-title">
            {t('lobby.recent')}
            <Link to="/profile" className="dim" style={{ fontSize: 'var(--fs-sm)' }}>
              {t('common.seeAll')}
            </Link>
          </h2>
          <div className="list">
            {recent.data.slice(0, 5).map((r) => (
              <div key={r.matchId} className="list-item">
                <span className="pill">{t(`game.${r.gameType}` as 'game.CALL_BREAK')}</span>
                <span className="grow dim">{f.relative(r.startedAt)}</span>
                {r.place && <span className="pill gold">#{r.place}</span>}
                <Coins amount={r.coinsDelta} signed size={16} className={r.coinsDelta >= 0 ? '' : 'dim'} />
              </div>
            ))}
          </div>
        </>
      )}

      <p className="muted" style={{ fontSize: 'var(--fs-xs)', textAlign: 'center', marginTop: 32 }}>
        {t('settings.legal')}
      </p>

      {ad.session && (
        <RewardedAdPlayer
          session={ad.session}
          onClose={() => {
            ad.close();
          }}
        />
      )}
      <Modal open={joinOpen} onClose={() => setJoinOpen(false)} title={t('rooms.join')}>
        <form
          className="col"
          onSubmit={(e) => {
            e.preventDefault();
            if (code.length >= 4) nav(`/rooms/${code.toUpperCase()}`);
          }}
        >
          <input className="input num" aria-label={t('rooms.enterCode')} placeholder={t('rooms.enterCode')} value={code} maxLength={8} onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))} autoFocus style={{ letterSpacing: '0.3em', textAlign: 'center', fontSize: 'var(--fs-xl)' }} />
          <button className="btn primary block" disabled={code.length < 4}>
            {t('rooms.join')}
          </button>
        </form>
      </Modal>
    </main>
  );
}
